import {roundTwoDigits} from "helpers/common"
import {formatDateOnly, toDateOnlyString} from "helpers/dateHelper"
import {getFullName} from "helpers/user"
import {
    buildCheckInCheckOutPeriods,
    CheckInCheckOutEvent,
    CheckInCheckOutStudent,
    getAttendedHoursFromPeriods
} from "sections/Admin/DailyAttendanceCheckInCheckOutReport/checkInCheckOutReport"

type AttendanceGroup = {
    events?: CheckInCheckOutEvent[]
}

type AttendanceExportRow = {
    profileId?: number
    customProfileId?: string
    studentName?: string
    timezone?: string
    attendedHours?: number | string
    missedHours?: number | string
    scheduleHours?: number | string
    date?: string
    events?: CheckInCheckOutEvent[]
    attendances?: AttendanceGroup[]
    student?: {
        id?: number
        customProfileId?: string
        firstName?: string
        middleName?: string
        lastName?: string
        nameSuffix?: string
        timezone?: string
    }
}

const toHours = (value: number | string | null | undefined) => {
    const hours = Number(value ?? 0)
    return Number.isFinite(hours) ? hours : 0
}

const collectAttendanceEvents = (row: AttendanceExportRow): CheckInCheckOutEvent[] => {
    const attendanceEvents = (row.attendances ?? []).flatMap((attendance) => attendance.events ?? [])
    if (attendanceEvents.length) {
        return attendanceEvents
    }
    return row.events ?? []
}

export const mapAttendanceRowToCheckInCheckOutStudent = (
    row: AttendanceExportRow,
    fallbackDate?: string
): CheckInCheckOutStudent => {
    const student = row.student
    const periods = buildCheckInCheckOutPeriods(collectAttendanceEvents(row))
    const isAbsent = periods.length === 0
    const storedAttendedHours = roundTwoDigits(toHours(row.attendedHours))
    const periodAttendedHours = getAttendedHoursFromPeriods(periods)
    const attendedHours = periodAttendedHours ? periodAttendedHours : storedAttendedHours
    const scheduleHours = roundTwoDigits(toHours(row.scheduleHours))
    const storedMissedHours = roundTwoDigits(Math.max(toHours(row.missedHours), 0))
    const missedHours = isAbsent
        ? storedMissedHours || scheduleHours
        : scheduleHours > 0
        ? roundTwoDigits(Math.max(scheduleHours - attendedHours, 0))
        : storedMissedHours

    return {
        profileId: student?.id ?? row.profileId,
        customProfileId: student?.customProfileId ?? row.customProfileId ?? "",
        studentName: getFullName(student) || row.studentName || "",
        date: toDateOnlyString(row.date) || toDateOnlyString(fallbackDate) || undefined,
        timezone: student?.timezone ?? row.timezone,
        attendedHours,
        missedHours,
        scheduleHours,
        isAbsent,
        periods
    }
}

export const formatCheckInCheckOutDateLabel = (
    from: string | undefined,
    to: string | undefined,
    dateFormat: string
) => {
    const start = formatDateOnly(from, dateFormat)
    const end = formatDateOnly(to, dateFormat)
    if (start && end && start !== end) {
        return `${start} - ${end}`
    }
    return start || end
}

export const getSelectedFilterNames = (items?: Array<{name?: string | null}> | null) =>
    (items ?? []).map((item) => item?.name).filter((name): name is string => !!name)
